<template>
  <div class="rank-bar">
    <RankBadge
      :key="stepKey"
      class="rank-bar-badge"
      :class="{ shifted: stepChanged }"
      :league="shownLeague"
      :division="shownDivision"
      :placement-remaining="ranked.placementRemaining"
      :size="badgeSize"
    />

    <template v-if="isPlacement">
      <div class="rank-pips" :aria-label="t('rank.placement')">
        <span
          v-for="i in PLACEMENT_GAMES"
          :key="i"
          class="rank-pip"
          :class="{ filled: i <= pipsFilled, fresh: i === played && pipsFilled >= i }"
        />
      </div>
      <p class="rank-bar-text">{{ t('rank.placementLeft', { n: ranked.placementRemaining }) }}</p>
    </template>

    <template v-else>
      <p class="rank-bar-name">
        {{ leagueName }}
        <span v-if="ranked.lpDelta !== 0" class="rank-bar-delta" :class="{ down: ranked.lpDelta < 0 }">
          {{ deltaLabel }}
        </span>
      </p>
      <div
        v-if="!isKing"
        class="rank-track"
        role="progressbar"
        :aria-valuenow="Math.round(value)"
        :aria-valuemin="0"
        :aria-valuemax="LP_PER_DIVISION"
        :aria-label="t('rank.lpProgress')"
      >
        <span
          class="rank-fill"
          :class="{ down: ranked.lpDelta < 0 }"
          :style="{ width: fillPct + '%' }"
        />
      </div>
      <p class="rank-bar-text">
        {{
          isKing
            ? t('rank.lpBare', { lp: Math.round(value) })
            : t('rank.lpCount', { lp: Math.round(value) })
        }}
      </p>
      <p v-if="stepChanged" class="rank-bar-shift" :class="{ down: demoted }">
        {{ demoted ? t('rank.demoted') : t('rank.promoted') }}
      </p>
      <p v-if="stepChanged && promoPacks > 0" class="rank-bar-packs">
        {{
          promoPacks === 1
            ? t('rank.promoPack')
            : t('rank.promoPacks', { n: promoPacks })
        }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import {
  LEAGUE_STEPS,
  LP_PER_DIVISION,
  PLACEMENT_GAMES,
  type RankedDelta,
} from '@shared/index'
import { useI18n } from '../i18n/t'
import RankBadge from './RankBadge.vue'
import {
  KING_STEP,
  buildLpSegments,
  ladderStepIndex,
  prefersReducedMotion,
  standingBefore,
  tweenSegments,
  type BarTween,
} from './progressTween'

const props = withDefaults(
  defineProps<{
    ranked: RankedDelta
    play: boolean
    badgeSize?: number
  }>(),
  { badgeSize: 72 },
)

const emit = defineEmits<{ done: [] }>()

const { t } = useI18n()

const before = computed(() => standingBefore(props.ranked))
const segments = computed(() => buildLpSegments(before.value, props.ranked))

const isPlacement = computed(
  () => props.ranked.placementRemaining > 0 || !props.ranked.league,
)
const played = computed(() => PLACEMENT_GAMES - props.ranked.placementRemaining)

const value = ref(0)
const stepIndex = ref(0)
const pipsFilled = ref(0)
const stepChanged = ref(false)
let tween: BarTween | null = null
let pipTimer = 0

const finalStep = computed(() =>
  ladderStepIndex(props.ranked.league, props.ranked.division),
)
const shownStep = computed(() =>
  isPlacement.value ? finalStep.value : stepIndex.value,
)
const shownLeague = computed(() =>
  isPlacement.value ? null : (LEAGUE_STEPS[shownStep.value]?.league ?? null),
)
const shownDivision = computed(() =>
  isPlacement.value ? null : (LEAGUE_STEPS[shownStep.value]?.division ?? null),
)
const stepKey = computed(() => `${shownLeague.value ?? 'none'}-${shownDivision.value ?? 0}`)

const isKing = computed(() => !isPlacement.value && shownStep.value === KING_STEP)
const demoted = computed(() => finalStep.value < ladderStepIndex(before.value.league, before.value.division))

const fillPct = computed(() =>
  Math.max(0, Math.min(100, (value.value / LP_PER_DIVISION) * 100)),
)

const leagueName = computed(() => {
  const league = shownLeague.value
  if (!league) return t('rank.placement')
  const name = t(`rank.leagues.${league}`)
  const division = shownDivision.value
  if (!division || league === 'king') return name
  return `${name} ${division === 1 ? 'I' : 'II'}`
})

const deltaLabel = computed(() => {
  const delta = props.ranked.lpDelta
  return delta > 0 ? `+${delta}` : String(delta)
})

const promoPacks = computed(() => props.ranked.promoPacks ?? 0)

function stop(): void {
  tween?.cancel()
  tween = null
  window.clearTimeout(pipTimer)
}

function skip(): void {
  if (tween) {
    tween.skip()
    return
  }
  window.clearTimeout(pipTimer)
  pipsFilled.value = played.value
}

function start(): void {
  stop()
  stepChanged.value = false

  if (isPlacement.value) {
    pipsFilled.value = Math.max(0, played.value - 1)
    if (prefersReducedMotion()) {
      pipsFilled.value = played.value
      emit('done')
      return
    }
    pipTimer = window.setTimeout(() => {
      pipsFilled.value = played.value
      emit('done')
    }, 420)
    return
  }

  const list = segments.value
  stepIndex.value = list[0]?.step ?? finalStep.value
  value.value = list[0]?.from ?? props.ranked.lp
  tween = tweenSegments(
    list,
    (next, segment, index) => {
      value.value = next
      stepIndex.value = segment.step
      if (index > 0) stepChanged.value = true
    },
    () => {
      tween = null
      if (list.length > 1) stepChanged.value = true
      emit('done')
    },
  )
}

watch(
  () => [props.play, props.ranked] as const,
  ([play]) => {
    if (play) start()
    else stop()
  },
  { immediate: true },
)

onBeforeUnmount(stop)

defineExpose({ skip })
</script>

<style scoped>
.rank-bar {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
}

.rank-bar-badge {
  transition: transform var(--md-motion-medium, 300ms)
    var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1));
}

.rank-bar-badge.shifted {
  animation: rankPop 520ms var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1));
}

.rank-bar-name {
  margin: 0;
  color: #fde68a;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.03em;
}

.rank-bar-delta {
  margin-left: 8px;
  color: #4ade80;
  font-size: 15px;
  font-weight: 800;
  animation: deltaRise 520ms var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1));
}

.rank-bar-delta.down {
  color: #f87171;
}

.rank-track {
  position: relative;
  width: 280px;
  height: 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
}

.rank-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #eab308, #facc15);
  box-shadow: 0 0 12px rgba(250, 204, 21, 0.55);
}

.rank-fill.down {
  background: linear-gradient(90deg, #b91c1c, #ef4444);
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.5);
}

.rank-bar-text {
  margin: 0;
  color: #e5e7eb;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.rank-bar-shift {
  margin: 0;
  color: #facc15;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  animation: deltaRise 520ms var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1));
}

.rank-bar-shift.down {
  color: #f87171;
}

.rank-bar-packs {
  margin: 0;
  color: #fde68a;
  font-size: 14px;
  font-weight: 700;
}

.rank-pips {
  display: flex;
  gap: 8px;
}

.rank-pip {
  width: 34px;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.18);
  transition: background var(--md-motion-medium, 300ms)
    var(--md-motion-standard, cubic-bezier(0.2, 0, 0, 1));
}

.rank-pip.filled {
  background: linear-gradient(90deg, #eab308, #facc15);
}

.rank-pip.fresh {
  animation: pipFlash 560ms var(--md-motion-emphasized, cubic-bezier(0.05, 0.7, 0.1, 1));
}

@keyframes rankPop {
  0% { transform: scale(0.6) rotate(-12deg); opacity: 0.2; }
  60% { transform: scale(1.18) rotate(3deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); }
}

@keyframes deltaRise {
  from { transform: translateY(8px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

@keyframes pipFlash {
  0% { transform: scaleX(0.3); box-shadow: none; }
  60% { transform: scaleX(1); box-shadow: 0 0 14px rgba(250, 204, 21, 0.7); }
  100% { transform: scaleX(1); box-shadow: 0 0 0 rgba(250, 204, 21, 0); }
}

@media (prefers-reduced-motion: reduce) {
  .rank-bar-badge,
  .rank-bar-badge.shifted,
  .rank-bar-delta,
  .rank-bar-shift,
  .rank-pip,
  .rank-pip.fresh {
    animation: none;
    transition: none;
  }
}
</style>
